import { useMemo } from 'react'
import { isAfter, isEqual, startOfDay } from 'date-fns'
import { slugs } from '@/constants/slugs'
import { useLocale } from '@/utils/LocaleUtil'
import { useTranslate } from '@/utils/translate/translate-client'
import { GiveawayFaq, useGiveawayFaqs } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayFaqs'

export enum GiveawayTabEnum {
  Details = 'Details',
  Updates = 'Updates',
  Faq = 'Faq',
  Prizes = 'Prizes',
  VacationPackages = 'VacationPackages',
}

export type GiveawayHowToStep = { title: string; description: string }
export type PartnerLogo = { src: string; alt: string }
export type GiveawayPrizeItem = { image: string; alt: string; title: string; value: string }
export type GalleryImage = { src: string; alt: string }
export type GiveawayTab = { title: string; type: GiveawayTabEnum }
export type GiveawayShare = { shareText: string; hashtag: string; shareImage: string }
export type CongratsModal = { text: string; image: string; alt: string }
export type SummaryPage = { image: string; alt: string }
export type ReservationCompletePage = { desktopImage: string; mobileImage: string; alt: string; text: string }

export type SuperFanBenefit = {
  badgeText?: string
  badgeClassName?: string
  imageUrl: string
  title: string
  description: string
  disclaimer?: string
}

export type GiveawayType = {
  tabs: GiveawayTab[]
  ticketFlow: {
    congratsModal: CongratsModal
    summaryPage: SummaryPage
    reservationCompletePage: ReservationCompletePage
  }
  seo: {
    cloudinaryImagePath: string
    cloudinaryTransformation: string
    title: string
    description: string
    path: string
    locale: string
  }
  share: GiveawayShare
  endDate: Date
  winnerAnnouncedDate: Date
  isActive: boolean
  trailerUrl: string
  giveawayTrailerUrl: string
  logoUrl: string
  inTheatersText: string
  videoButtonText: string
  backgroundVideoSrc?: string
  rulesLink: string
  heroTitle: string
  projectName: string
  backgroundHeroImage: {
    mobile: string
    desktop: string
  }
  steps: GiveawayHowToStep[]
  partnerLogos?: PartnerLogo[]
  faqs?: GiveawayFaq[]
  prizes?: GiveawayPrizeItem[]
  superFanBenefits?: SuperFanBenefit[]
  galleryImages: GalleryImage[]
  productTitleToFilter: string
  defaultTicketPackId: string
  entriesProductId: string
  videoImage?: string
}

interface Props {
  theatricalSlug: string
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useGiveawayData = ({ theatricalSlug }: Props): GiveawayType | null => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()

  const faqs = useGiveawayFaqs(theatricalSlug)

  const data = useMemo((): Omit<GiveawayType, 'isActive'> | null => {
    if (theatricalSlug === slugs.homestead) {
      return {
        tabs: [
          {
            title: t('prizeItems', 'Prize Items'),
            type: GiveawayTabEnum.Prizes,
          },
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('updates', 'Updates'),
            type: GiveawayTabEnum.Updates,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'btdCongratsModalText',
              "You've earned entries for your chance to win a $100k Dream Vacations for You and Your Favorite Teacher! Complete your checkout to secure your entries.",
            ),
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          summaryPage: {
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1733333895/100k-vacation-image-02.webp',
            mobileImage: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
            text: t(
              'btdReservationCompleteText',
              'You’ve earned entries into the Brave the Dark Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        share: {
          shareText:
            'The upcoming @AngelStudios movie Homestead in theaters on Dec. 20th. Pre-order tickets for a chance to win $300,000 in the #HomesteadSweepstakes.',
          hashtag: '#HomesteadSweepstakes',
          shareImage: 'v1730306592/homestead-key-art-main-wide-02.webp',
        },
        seo: {
          cloudinaryImagePath: 'v1730485364/Giveaway_Banner_Edit.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesV2', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
            projectName: 'Homestead',
          }),
          description: t(
            'homesteadTicketsSeoTitle',
            'Buy {{projectName}} tickets for a Chance to Win a $300K Giveaway!',
            {
              projectName: 'Homestead',
            },
          ),
          path: `/pre-sales/${theatricalSlug}/giveaway`,
          locale,
        },
        endDate: new Date(2024, 11, 22),
        winnerAnnouncedDate: new Date(2025, 0, 30),
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/V5zQ6XyHhrs',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/XURLw2XXHWA',
        logoUrl:
          'https://images.angelstudios.com/image/upload/v1703029504/studio-app/catalog/df2af424-2494-49f8-af2a-2299baa0b0cf.png',
        productTitleToFilter: 'Homestead Ticket',
        defaultTicketPackId: 'gid://shopify/Product/9042410799335',
        entriesProductId: 'gid://shopify/ProductVariant/50567413891303',
        backgroundVideoSrc: 'https://content.angelstudios.com/homestead/btd-video-loop-02.mp4',
        backgroundHeroImage: {
          mobile: 'https://images.angelstudios.com/image/upload/v1730306319/homestead-key-art-main-tall-02.webp',
          desktop: 'https://images.angelstudios.com/image/upload/v1730306263/homestead-key-art-main-wide-01.webp',
        },
        rulesLink: 'https://bit.ly/HomesteadSweeps',
        heroTitle: t('enterHomesteadGiveawayV3', 'Watch Homestead in Theaters and You could Win a $300k Homestead!'),
        inTheatersText: t('inTheatersDec20', 'In theaters dec. 20th'),
        videoButtonText: t('watchGiveawayVideo', 'Watch Giveaway Video'),
        projectName: 'Homestead',
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t(
              'preOrderTicketsStep1Description',
              'Choose a ticket pack from our exclusive pre-order options.',
            ),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2Description',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3Title', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3Description',
              'The earlier you pre-order, the higher your multiplier for the $300k Homestead Giveaway.',
            ),
          },
        ],
        partnerLogos: [
          {
            src: '/v1727730787/Homestead/partners/boxhouse-logo-gray-2x.png',
            alt: 'boxhouse-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/black-beard-logo-gray-2x.png',
            alt: 'black-beard-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/bakcou-logo-gray-2x.png',
            alt: 'bakcou-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/black-rifle-coffee-company-logo-gray-2x.png',
            alt: 'black-rifle-coffee-company-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/gatorz-logo-gray-2x.png',
            alt: 'gatorz-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/goldbacks-logo-gray-2x.png',
            alt: 'goldbacks-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/fix-it-sticks-logo-gray-2x.png',
            alt: 'fix-it-sticks-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/grip6-logo-gray-2x.png',
            alt: 'grip6-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/knafs-logo-gray-2x.png',
            alt: 'knafs-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/lion-energy-logo-gray2.png',
            alt: 'lion-energy-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/kaito-logo-gray-2x.png',
            alt: 'kaito-logo',
          },
          {
            src: '/v1727730787/Homestead/partners/mira-safety-logo-gray-2x.png',
            alt: 'mira-safety-logo',
          },
          {
            src: '/v1727730788/Homestead/partners/my-medic-logo-gray-2x.png',
            alt: 'my-medic-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/nutrimill-logo-gray-2x.png',
            alt: 'nutrimill-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/oberwerk-logo-gray-2x.png',
            alt: 'oberwerk-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/nutristore-logo-gray-2x.png',
            alt: 'nutristore-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/olli-logo-gray-2x.png',
            alt: 'olli-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/planta-logo-gray-2x.png',
            alt: 'planta-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/protek-logo-gray-2x.png',
            alt: 'protek-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/rockwell-watch-logo-gray-2x.png',
            alt: 'rockwell-watch-logo',
          },
          {
            src: '/v1727730790/Homestead/partners/rockwell-water-logos-gray-2x.png',
            alt: 'rockwell-water-logo',
          },
          {
            src: '/v1727730791/Homestead/partners/sparks-motors-logo-gray-2x.png',
            alt: 'sparks-motors-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/thirsty-earth-logo-gray-2x.png',
            alt: 'thirsty-earth-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/uncharted-supply-logo-gray2.png',
            alt: 'uncharted-supply-logo',
          },
          {
            src: '/v1727730793/Homestead/partners/x-tac-logo-gray-2x.png',
            alt: 'x-tac-logo',
          },
        ],
        faqs,
        prizes: [
          {
            image: '/v1727735733/Homestead/prizes/homestead-boxhouse-01.webp',
            alt: 'Box House',
            title: 'Box House 500 Sq. Ft. Tiny House',
            value: '$139,000',
          },
          {
            image: '/v1727735730/Homestead/prizes/DSC06697.webp',
            alt: '',
            title: 'Polaris Xpedition OHV Customized by the Diesel Brothers',
            value: '$75,000',
          },
          {
            image: '/v1727735729/Homestead/prizes/bitcoin-01.webp',
            alt: '',
            title: 'One Bitcoin (BTC)',
            value: '> $90,000',
          },
          {
            image: '/v1727735723/Homestead/prizes/AngelStudio_09172413178.webp',
            alt: '',
            title: 'Bakcou Full Suspension eBike',
            value: '$5,200',
          },
          {
            image: '/v1727735721/Homestead/prizes/AngelStudio_09172413077.webp',
            alt: '',
            title: 'NutriStore Freeze Dried 6-Month Food Supply',
            value: '$4,438',
          },
          {
            image: '/v1727735740/Homestead/prizes/sungrow-greenhouse-sungrow-20.webp',
            alt: '',
            title: 'Planta Greenhouses - Sungrow 20',
            value: '$3,800',
          },
          {
            image: '/v1727735737/Homestead/prizes/IMG_8556.webp',
            alt: '',
            title: 'PlayXTAC Four Person Tactical Laser Airsoft Kit',
            value: '$2,304',
          },
          {
            image: '/v1727735735/Homestead/prizes/homestead-rockwell-water-01.webp',
            alt: '',
            title: 'Two 250-Gallon QuakeTank Water Storage Tanks',
            value: '$1,800',
          },
          {
            image: '/v1727735725/Homestead/prizes/AngelStudio_09172413239.webp',
            alt: '',
            title: 'Lion Energy Portable Solar Power Unit (Lion Safari)',
            value: '$1,500',
          },
          {
            image: '/v1727735725/Homestead/prizes/AngelStudio_09172413261.webp',
            alt: '',
            title: 'Knafs Lulu Knife + Gear Bundle',
            value: '$750',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413112.webp',
            alt: '',
            title: 'NutriMill - Mill & Mixer',
            value: '$749',
          },
          {
            image: '/v1727735726/Homestead/prizes/AngelStudio_09172413276.webp',
            alt: '',
            title: 'Thirsty Earth: CottaCup Automatic Olla Watering System & Three Planter Boxes',
            value: '$700',
          },
          {
            image: '/v1727735728/Homestead/prizes/AngelStudio_09172413351.webp',
            alt: '',
            title: 'Three Pair of GATORZ Eyewear',
            value: '$650',
          },
          {
            image: '/v1727735732/Homestead/prizes/DSC07534.webp',
            alt: '',
            title: 'Oberwerk 20x65 ED Binocular on Oberwerk 4000 Tripod',
            value: '$600',
          },
          {
            image: '/v1727735723/Homestead/prizes/AngelStudio_09172413192.webp',
            alt: '',
            title: 'Uncharted Supply Co - SEVENTY2 Pro Survival System',
            value: '$589',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413091.webp',
            alt: '',
            title: '100 Utah GoldBacks - Pure Gold',
            value: '$542',
          },
          {
            image: '/v1729197021/IMG_8555.webp',
            alt: '',
            title: 'Rockwell Time - Ruck Backpack, Sunglasses, Apparel',
            value: '$500',
          },
          {
            image: '/v1729193204/plusefire-flamethrower-01.webp',
            alt: '',
            title: 'Exothermic Technologies PulseFire Long Range Torch',
            value: '$499',
          },
          {
            image: '/v1727735731/Homestead/prizes/DSC07533.webp',
            alt: '',
            title: 'ISOtunes Hearing Protection - Four Advance BT Earbuds + Two Defy Slim Basic Earmuffs',
            value: '$460',
          },
          {
            image: '/v1727735729/Homestead/prizes/AngelStudio_09172413393.webp',
            alt: '',
            title: 'PROTEK Watches USMC Dive Watch',
            value: '$450',
          },
          {
            image: '/v1727735727/Homestead/prizes/AngelStudio_09172413308.webp',
            alt: '',
            title: 'MIRA Safety EvakPak',
            value: '$370',
          },
          {
            image: '/v1727735733/Homestead/prizes/homestead-grip6-gear-01.webp',
            alt: '',
            title: 'GRIP6 - Belts, Wallets, and Socks',
            value: '$350',
          },
          {
            image: '/v1727735727/Homestead/prizes/AngelStudio_09172413344.webp',
            alt: '',
            title: 'Fix It Sticks - All-in-One Toolkit',
            value: '$292',
          },
          {
            image: '/v1727735735/Homestead/prizes/IMG_8483.webp',
            alt: '',
            title: 'MyMedic MyFAK Pro Full Medical Kit',
            value: '$227',
          },
          {
            image: '/v1727735724/Homestead/prizes/AngelStudio_09172413223.webp',
            alt: '',
            title: 'Black Beard Fire - Ultimate Fire Kit',
            value: '$225',
          },
          {
            image: '/v1727735738/Homestead/prizes/olli-apparel-img-01.webp',
            alt: '',
            title: 'Olli Bottoms of Your Choice, Olli Top of Your Choice, and Olli Headband',
            value: '$137',
          },
          {
            image: '/v1727735738/Homestead/prizes/katio-radio-02.webp',
            alt: '',
            title: 'Kaito Voyager Pro KA600',
            value: '$90',
          },
          {
            image: '/v1727735722/Homestead/prizes/AngelStudio_09172413103.webp',
            alt: '',
            title: 'Black Rifle Coffee Gift Pack',
            value: '$70',
          },
        ],
        superFanBenefits: [
          {
            badgeText: t('digitalBookBenefit', 'Digital Book'),
            imageUrl: 'https://images.angelstudios.com/image/upload/v1730479729/book-cover-perspective-view-03.jpg',
            title: t('digitalBookTitle', 'Digital Book - Homestead: Black Autumn. Faith and Family Edition'),
            description: t(
              'digitalBookDescription',
              'Prepare for the film event in the best way you can, with the bestselling novel that started it all.',
            ),
          },
          {
            badgeText: t('audioBookBenefit', 'Audio Book'),
            badgeClassName: '!bg-[#F0EEE7]',
            imageUrl: 'https://images.angelstudios.com/image/upload/v1730479680/homestead-audio-book-cover-01.png',
            title: t('audioBookTitle', 'Audio Book - Homestead: Black Autumn. Faith and Family Edition'),
            description: t(
              'audioBookDescription',
              'Listen anywhere to the bestselling novel behind Homestead, and dive into the film’s world before you even pick up your popcorn.',
            ),
          },
          {
            imageUrl: 'https://images.angelstudios.com/image/upload/v1725484357/name-in-credits-img-02.webp',
            title: t('nameInCreditsTitleV2', 'Your Name in the Credits for Streaming'),
            description: t(
              'nameInCreditsDescriptionV2',
              'Make your mark on Homestead’s release. Have your name featured in the credits—on our streaming platforms, DVD/Blu-ray, and alongside the community who brought this film to life.',
            ),
          },
          {
            badgeText: t('eBookBenefit', 'e-Book'),
            badgeClassName: '!bg-[#F0EEE7]',
            imageUrl:
              'https://images.angelstudios.com/image/upload/v1726153403/angel-studios/guild/homestead/homestead-prep-bundle-mock-01.webp',
            title: t('eBookBundleTitle', 'eBook Bundle - Homesteading & Preparedness Resource Library'),
            description: t(
              'eBookBundleDescription',
              'Enhance your library with the ultimate DIY homesteading and preparedness collection. This comprehensive eBook bundle equips you with essential skills for any just-in-case scenario.',
            ),
          },
          {
            imageUrl: 'https://images.angelstudios.com/image/upload/v1725484408/nmcd-red-carpet-image-01.webp',
            title: t('redCarpetPremiereTitle', 'Two Red Carpet Tickets to the Homestead Movie Premiere in Utah'),
            description: t(
              'redCarpetPremiereDescription',
              'Strut the red carpet like the film insider you are, and enjoy a once-in-a-lifetime experience when you attend the Utah premiere of Homestead!',
            ),
            disclaimer: t('redCarpetPremiereDisclaimer', 'Does not include travel, lodging, and food expenses.'),
          },
          {
            imageUrl:
              'https://images.angelstudios.com/image/upload/v1726078846/angel-studios/guild/homestead/homestead-site-01.webp',
            title: t('setVisitTitle', 'Invitation to Visit the Homestead Property During Filming'),
            description: t(
              'setVisitDescription',
              'Join the cast and crew of Homestead on-location in Utah during the filming of the TV series, with exclusive access for you and a guest.',
            ),
            disclaimer: t(
              'setVisitDisclaimer',
              'Does not include travel, lodging, and food expenses. Although you’ll have on-location access, we cannot promise interactions with the cast or crew.',
            ),
          },
        ],
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952139/angel-studios/guild/homestead/homestead-img-01.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-04.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952141/angel-studios/guild/homestead/homestead-img-02.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952142/angel-studios/guild/homestead/homestead-img-07.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952143/angel-studios/guild/homestead/homestead-img-08.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952144/angel-studios/guild/homestead/homestead-img-09.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-05.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952145/angel-studios/guild/homestead/homestead-img-10.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952151/angel-studios/guild/homestead/homestead-img-06.webp',
            alt: 'Homestead Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/c_scale,w_1200/v1724952148/angel-studios/guild/homestead/homestead-img-03.webp',
            alt: 'Homestead Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.braveTheDark) {
      return {
        heroTitle: t(
          'giveawayHeroTitleBraveAlt',
          'Watch Brave the Dark in Theaters and Unlock a Chance to Win a $200k Dream Vacation',
        ),
        logoUrl:
          'https://images.angelstudios.com/image/upload/v1732124324/studio-app/catalog/94847f75-91ef-43ac-9c59-5ced2b9d9240.webp',
        inTheatersText: t('inTheatersBraveTheDark', 'In theaters Jan. 24th'),
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/knvKDPwWo3I',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/643rJ7pl5p4',
        backgroundVideoSrc: 'https://content.angelstudios.com/brave-the-dark/btd-video-loop-01.mp4',
        videoImage: '/v1733333895/100k-vacation-image-02.webp',
        backgroundHeroImage: {
          mobile: 'https://images.angelstudios.com/image/upload/v1734061752/brave-the-dark-vert-hero-img-01.webp',
          desktop: 'https://images.angelstudios.com/image/upload/v1734061720/brave-the-dark-key-horiz-01.webp',
        },
        share: {
          shareText:
            'The upcoming @AngelStudios movie Brave the Dark in theaters on Jan. 24th. Pre-order tickets for as chance to Win $100k Dream Vacations for You and Your Favorite Teacher in the #DreamGiveawaySweepstakes.',
          hashtag: '#DreamGiveawaySweepstakes',
          shareImage: 'v1734115533/brave-the-dark/ca4d3b1841bdc4ad201c3001746f8526.webp',
        },
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 4),
        videoButtonText: t('watchVideoGiveaway', 'Watch Video'),
        projectName: 'Brave the Dark',
        productTitleToFilter: 'Brave the Dark Ticket',
        defaultTicketPackId: 'gid://shopify/Product/9065209790695',
        entriesProductId: 'gid://shopify/ProductVariant/50567428440295',
        rulesLink: 'https://bit.ly/200KDreamGiveaway',
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t(
              'preOrderTicketsStep1Description',
              'Choose a ticket pack from our exclusive pre-order options.',
            ),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2Description',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3Title', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3DescriptionBrave',
              'The earlier you pre-order, the higher your multiplier for the $200k Dream Vacation Giveaway.',
            ),
          },
        ],
        tabs: [
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'BtDCongratsModalText',
              "You've earned entries for your chance to win a $200,000 dream vacation! Complete your checkout to secure your entries.",
            ),
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          summaryPage: {
            image: 'v1735863876/brave-the-dark/dream-vacation-giveaway-sq-img-01.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            mobileImage: '/v1735864465/brave-the-dark/dream-vacation-giveaway-sq-img-01b_1.png',
            alt: '',
            text: t(
              'btdReservationCompleteText',
              'You’ve earned entries into the Brave the Dark Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        seo: {
          cloudinaryImagePath: 'v1734110113/open-graph-brave-giveaway-241212.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesV2', 'PRE-ORDER {{projectName}} Tickets | Angel Studios', {
            projectName: 'Brave the Dark',
          }),
          description: t(
            'braveTheDarkTicketsSeoTitle',
            'Buy {{projectName}} tickets for a Chance to Win $100K Dream Vacations!',
            {
              projectName: 'Brave the Dark',
            },
          ),
          path: `/pre-sales/${theatricalSlug}/giveaway`,
          locale,
        },
        faqs,
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-01.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-02.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-03.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-04.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-05.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-06.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-07.webp',
            alt: 'Brave The Dark Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1733265728/btd-gallery-img-08.webp',
            alt: 'Brave The Dark Images',
          },
        ],
      }
    }

    if (theatricalSlug === slugs.ruleBreakers) {
      return {
        heroTitle: t(
          'giveawayHeroTitleRuleBreakersV3',
          'Watch Rule Breakers in Theaters and Unlock a Chance to Win a $200k Dream Vacation.',
        ),
        endDate: new Date(2025, 2, 9),
        winnerAnnouncedDate: new Date(2025, 3, 4),
        seo: {
          cloudinaryImagePath: 'v1734728880/open-graph-rulebreakers-giveaway-241220.webp',
          cloudinaryTransformation: 'b_rgb:000000,c_fill',
          title: t('ticketsSEODescriptionPreSalesRuleBreakers', 'PRE-ORDER Rule Breakers Tickets | Angel Studios'),
          description: t(
            'ruleBreakerTicketsSeoTitle',
            'Buy Rule Breakers tickets for a Chance to Win $100K Dream Vacations!',
          ),
          path: `/pre-sales/${theatricalSlug}/giveaway`,
          locale,
        },
        faqs,
        logoUrl: 'https://images.angelstudios.com/image/upload/v1734631651/rule-breakers-logo_1.webp',
        inTheatersText: t('inTheatersRuleBreakers', 'In theaters Mar. 7th'),
        share: {
          shareText:
            'The upcoming @AngelStudios movie Brave the Dark in theaters on Jan. 24th. Pre-order tickets for as chance to Win $100k Dream Vacations for You and Your Favorite Teacher in the #DreamGiveawaySweepstakes.',
          hashtag: '#DreamGiveawaySweepstakes',
          shareImage: 'v1734728616/rule-breakers-title-art-horiz-06.webp',
        },
        rulesLink: 'https://bit.ly/200KDreamGiveaway',
        videoImage: '/v1734728695/200k-vacation-image-01.webp',
        backgroundVideoSrc: 'https://content.angelstudios.com/rule-breakers/rule-breakers-video-loop2.mp4',
        defaultTicketPackId: 'gid://shopify/Product/9074595987687',
        entriesProductId: 'gid://shopify/ProductVariant/50586232881383',
        giveawayTrailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        trailerUrl: 'https://www.youtube-nocookie.com/embed/CW_P4zT6i9A',
        videoButtonText: t('watchVideoGiveawayRule', 'Watch Video'),
        projectName: 'Rule Breakers',
        productTitleToFilter: 'Rule Breakers Ticket',
        backgroundHeroImage: {
          desktop: 'https://images.angelstudios.com/image/upload/v1734728548/rule-breakers-title-art-horiz-03b.webp',
          mobile: 'https://images.angelstudios.com/image/upload/v1734728586/rule-breakers-title-art-horiz-04.webp',
        },
        steps: [
          {
            title: t('preOrderTicketsStep1Title', 'Pre-Order Tickets'),
            description: t(
              'preOrderTicketsStep1DescriptionRule',
              'Choose a ticket pack from our exclusive pre-order options.',
            ),
          },
          {
            title: t('preOrderTicketsStep2Title', 'Get Your Promo Codes'),
            description: t(
              'preOrderTicketsStep2DescriptionRule',
              'Receive promo codes equal to your ticket quantity, redeemable when showtimes are available.',
            ),
          },
          {
            title: t('preOrderTicketsStep3TitleRule', 'Multiply Your Chances'),
            description: t(
              'preOrderTicketsStep3DescriptionBrave',
              'The earlier you pre-order, the higher your multiplier for the $200k Dream Vacation Giveaway.',
            ),
          },
        ],
        tabs: [
          {
            title: t('details', 'Details'),
            type: GiveawayTabEnum.Details,
          },
          {
            title: t('faq', 'FAQ'),
            type: GiveawayTabEnum.Faq,
          },
        ],
        ticketFlow: {
          congratsModal: {
            text: t(
              'ruleBreakersCongratsModalText',
              "You've earned entries for your chance to win a $100k Dream Vacations for You and Your Favorite Teacher! Complete your checkout to secure your entries.",
            ),
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          summaryPage: {
            image: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
          },
          reservationCompletePage: {
            desktopImage: '/v1733333895/100k-vacation-image-02.webp',
            mobileImage: '/v1733333895/100k-vacation-image-02.webp',
            alt: '',
            text: t(
              'ruleBreakersReservationCompleteText',
              'You’ve earned entries into the Rule Breakers Giveaway for a chance to win the $100k Dream Vacations for You and Your Favorite Teacher!',
            ),
          },
        },
        galleryImages: [
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g01.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g02.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g03.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g04.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g05.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g06.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g07.webp',
            alt: 'Rule Breakers Images',
          },
          {
            src: 'https://images.angelstudios.com/image/upload/v1734583396/rule-breakers-g08.webp',
            alt: 'Rule Breakers Images',
          },
        ],
      }
    }

    return null
  }, [faqs, locale, t, theatricalSlug])

  if (!data) return null

  return { ...data, isActive: isGiveawayActive(data.endDate) } as GiveawayType
}

export const isGiveawayActive = (endDate: Date) => {
  const startOfEndDate = startOfDay(endDate)
  const startOfToday = startOfDay(new Date())

  return isEqual(startOfEndDate, startOfToday) || isAfter(startOfEndDate, startOfToday)
}
